import { ReactNode, useEffect, useMemo } from 'react'
import {
  AtendimentoType,
  initialQueryState,
  OptionsHelper,
  QueryFilter,
  useDebounce,
} from '../../../../../../../_metronic/helpers'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useListFilters } from '../../core/ListFiltersProvider'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  Input,
  IOption,
  TableFilter,
  TableFilters,
} from '../../../../../../components'
import { useT } from '_metronic/i18n/Metronici18n'

type Props = {
  /** Conteúdo à direita da linha dos campos (ex.: "Adicionar"). */
  actions?: ReactNode
}

export const EspecialidadeListSearchComponent = ({ actions }: Props) => {
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const { t } = useT()
  const total = response?.totalElements ?? 0

  const debouncedFilters = useDebounce(filters, 500)

  const search = useMemo(
    () => (debouncedFilters.nome ? `nome~*${debouncedFilters.nome}*` : ''),
    [debouncedFilters]
  )

  // O tipo de atendimento vai em `filters` (tipo), não no `search`.
  const filtersTipo = useMemo(
    (): QueryFilter[] =>
      debouncedFilters.tipoAtendimento?.value
        ? [{ field: 'tipo', operator: '=', value: String(debouncedFilters.tipoAtendimento.value) }]
        : [],
    [debouncedFilters]
  )

  useEffect(() => {
    updateState({
      ...initialQueryState,
      search,
      filters: filtersTipo,
    })
  }, [search, filtersTipo, updateState])

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo.
  const activeFilters: ActiveFilter[] = [
    filters.nome && {
      key: 'nome',
      label: t('especialidade.label'),
      value: `“${filters.nome}”`,
      onRemove: () => setFilter('nome', ''),
    },
    filters.tipoAtendimento && {
      key: 'tipoAtendimento',
      label: 'Tipo de atendimento',
      value: filters.tipoAtendimento.label,
      onRemove: () => setFilter('tipoAtendimento', undefined),
    },
  ].filter((filter): filter is ActiveFilter => Boolean(filter))

  return (
    <TableFilters
      actions={actions}
      footer={
        <ActiveFiltersBar
          filters={activeFilters}
          onClearAll={clearFilters}
          summary={
            isLoading
              ? 'Buscando…'
              : `${total} ${
                  total === 1 ? 'especialidade encontrada' : 'especialidades encontradas'
                }`
          }
        />
      }
    >
      <TableFilter label={t('especialidade.label')} wide>
        <Input
          entity='especialidade'
          value={filters.nome}
          onChange={(e) => setFilter('nome', e.target.value)}
        />
      </TableFilter>
      <TableFilter label={t('tipoAtendimento.label')}>
        <CustomSelect
          entity='tipoAtendimento'
          onChange={(tipo?: IOption<AtendimentoType>) =>
            setFilter('tipoAtendimento', tipo ?? undefined)
          }
          fetchOptions={OptionsHelper.getOptionsTiposAtendimento}
          value={filters.tipoAtendimento}
        />
      </TableFilter>
    </TableFilters>
  )
}
